import { useState } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import useGetTrialDocuments from "../../hooks/useGetTrialDocuments";
import useCreateTrialDocumentMutation from "../../hooks/useCreateTrialDocumentMutation";
import useUpdateTrialDocumentMutation from "../../hooks/useUpdateTrialDocumentMutation";
import useDeleteTrialDocumentMutation from "../../hooks/useDeleteTrialDocumentMutation";
import Loader from "../Loader";
import CustomButton from "../UI/CustomButton";

const getFileExtension = (url) => {
  if (!url) return "";
  const path = url.split("?")[0];
  const match = path.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toLowerCase() : "";
};

const isImageUrl = (url) =>
  ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(getFileExtension(url));

const isPdfUrl = (url) => getFileExtension(url) === "pdf";

const fmtDateTime = (value) => (value ? dayjs(value).format("MMM D, YYYY h:mm A") : "—");

const headerCellSx = {
  backgroundColor: (theme) => theme.palette.grey[100],
  fontWeight: "bold",
  textTransform: "uppercase",
  fontSize: "0.85rem",
  color: "#424242",
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  fontSize: "0.8rem",
  verticalAlign: "top",
};

const TrialDocuments = ({ trialId }) => {
  const [showDeleted, setShowDeleted] = useState(false);
  const { data: documents = [], isLoading } = useGetTrialDocuments({ trialId, showDeleted });
  const createMutation = useCreateTrialDocumentMutation();
  const updateMutation = useUpdateTrialDocumentMutation();
  const deleteMutation = useDeleteTrialDocumentMutation();

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newDoc, setNewDoc] = useState({ name: "", description: "", file: null });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDocument, setViewDocument] = useState({ url: "", name: "" });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const closeAddModal = () => {
    setAddModalOpen(false);
    setNewDoc({ name: "", description: "", file: null });
  };

  const handleUpload = () => {
    if (!newDoc.name || !newDoc.file) return;
    createMutation.mutate(
      { trialId, name: newDoc.name, description: newDoc.description, file: newDoc.file },
      { onSuccess: closeAddModal }
    );
  };

  const startEdit = (document) => {
    setEditingId(document.id);
    setEditForm({ name: document.name, description: document.description || "" });
  };

  const saveEdit = (document) => {
    updateMutation.mutate(
      { documentId: document.id, trialId, name: editForm.name, description: editForm.description },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleViewClick = (document) => {
    setViewDocument({ url: document.file, name: document.name });
    setViewOpen(true);
  };

  const handleDeleteClick = (document) => {
    setDocumentToDelete(document);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate(
      { documentId: documentToDelete.id, trialId },
      {
        onSettled: () => {
          setConfirmDeleteOpen(false);
          setDocumentToDelete(null);
        },
      }
    );
  };

  if (isLoading) return <Loader />;

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              slotProps={{ input: { role: "switch", "data-testid": "show-deleted-toggle" } }}
            />
          }
          label="Show deleted documents"
        />
        <CustomButton
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
          data-testid="add-document-btn"
        >
          Add Document
        </CustomButton>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto", mb: 2 }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Name</TableCell>
              <TableCell sx={headerCellSx}>Description</TableCell>
              <TableCell sx={headerCellSx}>Uploaded At</TableCell>
              <TableCell sx={headerCellSx}>Uploaded By</TableCell>
              <TableCell sx={headerCellSx}>Deleted At</TableCell>
              <TableCell sx={headerCellSx}>Deleted By</TableCell>
              <TableCell sx={headerCellSx}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    No documents attached to this trial yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              documents.map((document) => {
                const isDeleted = Boolean(document.deleted_at);
                const isEditing = editingId === document.id;
                return (
                  <TableRow
                    key={document.id}
                    data-testid="document-row"
                    data-document-name={document.name}
                    sx={isDeleted ? { opacity: 0.6, backgroundColor: "action.hover" } : undefined}
                  >
                    <TableCell sx={bodyCellSx}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          fullWidth
                        />
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {document.name}
                          </Typography>
                          {isDeleted && <Chip label="Deleted" size="small" color="default" />}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, maxWidth: 280 }}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          value={editForm.description}
                          onChange={(e) =>
                            setEditForm({ ...editForm, description: e.target.value })
                          }
                          multiline
                          fullWidth
                        />
                      ) : document.description ? (
                        <Tooltip title={document.description}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {document.description}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{fmtDateTime(document.created_at)}</TableCell>
                    <TableCell sx={bodyCellSx}>{document.uploaded_by || "—"}</TableCell>
                    <TableCell sx={bodyCellSx}>{fmtDateTime(document.deleted_at)}</TableCell>
                    <TableCell sx={bodyCellSx}>{document.deleted_by || "—"}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      {isDeleted ? (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      ) : isEditing ? (
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => saveEdit(document)}
                            data-testid="document-save-edit-btn"
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setEditingId(null)}
                            data-testid="document-cancel-edit-btn"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewClick(document)}
                            data-testid="document-view-btn"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            component="a"
                            href={document.file}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => startEdit(document)}
                            data-testid="document-edit-btn"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(document)}
                            data-testid="document-delete-btn"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={addModalOpen} onClose={closeAddModal} maxWidth="sm" fullWidth>
        <DialogTitle>Add Document</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Name"
              value={newDoc.name}
              onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
              required
              inputProps={{ "data-testid": "document-name-input" }}
            />
            <TextField
              size="small"
              label="Description (optional)"
              value={newDoc.description}
              onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
              multiline
              minRows={3}
              inputProps={{ "data-testid": "document-description-input" }}
            />
            <Button variant="outlined" component="label">
              {newDoc.file ? newDoc.file.name : "Choose File"}
              <input
                type="file"
                hidden
                data-testid="document-file-input"
                onChange={(e) => setNewDoc({ ...newDoc, file: e.target.files[0] })}
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddModal} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <CustomButton
            size="small"
            onClick={handleUpload}
            isLoading={createMutation.isPending}
            disabled={!newDoc.name || !newDoc.file || createMutation.isPending}
            data-testid="upload-document-submit-btn"
          >
            Upload
          </CustomButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="subtitle1" noWrap sx={{ maxWidth: "85%" }}>
            {viewDocument.name}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setViewOpen(false)}
            data-testid="close-document-view-btn"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          {isImageUrl(viewDocument.url) ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "auto",
              }}
            >
              <img
                src={viewDocument.url}
                alt={viewDocument.name}
                style={{ maxWidth: "100%", maxHeight: "100%" }}
              />
            </Box>
          ) : isPdfUrl(viewDocument.url) ? (
            <iframe
              src={viewDocument.url}
              title={viewDocument.name}
              style={{ flex: 1, width: "100%", border: "none" }}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Typography color="text.secondary">
                Preview not available for this file type.
              </Typography>
              <Button
                variant="outlined"
                component="a"
                href={viewDocument.url}
                target="_blank"
                rel="noreferrer"
                startIcon={<DownloadIcon fontSize="small" />}
              >
                Download to view
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Are You Sure?</DialogTitle>
        <DialogContent>
          <Typography>You want to delete this document?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            color="inherit"
            disabled={deleteMutation.isPending}
          >
            No
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            data-testid="confirm-delete-document-btn"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrialDocuments;
